import { ChangeDetectorRef, Component, HostListener, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Subscription } from 'rxjs';

import { FormsBuilderStateService } from '../services/forms-builder-state.service';
import { FormsBuilderPolishedService } from '../services/forms-builder-polished.service';
import { AiDraftApplyContext, FormsBuilderAiDraftService } from '../services/forms-builder-ai-draft.service';
import { FormsBuilderTrashService } from '../services/forms-builder-trash.service';
import { FormsService } from '../../forms.service';
import { FormsGlobals } from '../../../../models/forms/forms.enum';
import { StaticDataService } from '../../../../services/static-data.service';

import { FormConfigurationModel } from '../../../../models/forms/form-configuration.model';
import { FormVersionConfigurationModel } from '../../../../models/forms/form-version-configuration.model';
import { EnumValidValueItemModel } from '../../../../models/enum/enum-valid-value-item.model';
import { TrashEntry } from '../models/trash-entry.model';

import { FormsBuilderPolishedFormCreationComponent } from '../forms-builder-polished-form-creation/forms-builder-polished-form-creation.component';
import { FormsBuilderPolishedConfirmComponent } from '../forms-builder-polished-confirm/forms-builder-polished-confirm.component';
import {
  ProcedureFormMismatchDialogComponent
} from '../../../../dialog/procedure-form-mismatch-dialog/procedure-form-mismatch-dialog.component';
import { PrintPreviewComponent } from '../print-preview/print-preview.component';
import { FormsBuilderTutorialComponent } from '../tutorial/forms-builder-tutorial.component';
import { FormsBuilderAiStudioDialogComponent } from '../forms-builder-ai-studio-dialog/forms-builder-ai-studio-dialog.component';
import { FormsBuilderAiImportReviewDialogComponent } from '../forms-builder-ai-import-review-dialog/forms-builder-ai-import-review-dialog.component';
import { AiGeneratedFormDraft } from '../models/ai-form-draft.model';
import { UiService } from 'src/app/services/ui.service';

/**
 * fetchCfgForm rejection reason meaning "the backend has no row for this DFormId".
 * getCfgDForm answers 200-with-a-null-body (not 404) for an unknown id, so this is the only
 * signal that a form exists client-side only — distinguishing it from a transport failure
 * keeps us from telling the user to save a form when the real problem is the network.
 */
const FORM_NOT_FOUND = 'form-not-found';

/**
 * Hover copy for the Form Properties panel. Kept here rather than inline in the template so the
 * wording lives in one reviewable place — every one of these fields is otherwise a bare label with
 * nothing on screen explaining what it does.
 */
const FORM_PROPERTY_HELP = {
  formName: 'The name staff see when searching for this form and adding it to a case. ' +
    'Applies to every version of the form.',
  category: 'Groups the form in the form list for searching and filtering. ' +
    'Does not change how the form behaves.',
  dataSource: 'Generic forms are the ones you build here with paragraphs and items. ' +
    'Custom links to one of TDO\'s built-in clinical screens (Perio, MTI, Diagnosis, etc.) ' +
    'instead — custom forms have no versions and cannot be edited in the builder.',
  customType: 'Which built-in TDO form this entry maps to. Only used when Data Source is Custom.',
  requireSignature: 'Marks the form as one that should be signed. Shows as "Need Signature" on the ' +
    'patient\'s form details. This is a label only — it does not add a signature field or block ' +
    'completion. To actually require a signature, add a Signature item and mark it Required.',
  requireTooth: 'The form must be tied to a tooth. Staff are prompted with the tooth picker when ' +
    'adding or opening the form on a case, and cannot add the form without selecting one.',
  requireSubmission: 'Adds a "Complete Form" button at the bottom of the form so staff explicitly ' +
    'submit it. Without this, the form has no submit step — entries are just saved as staff fill it in.'
};

@Component({
  standalone: false,
  selector: 'app-form-finder',
  templateUrl: './form-finder.component.html',
  styleUrls: ['./form-finder.component.scss']
})
export class FormFinderComponent implements OnInit, OnDestroy {
  TDOCID: number = 0;
  private isDestroyed = false;
  isLoading: boolean = false;
  isSaving: boolean = false;
  isSearchingForForms: boolean = false;
  cfgFormIsLoading: boolean = false;

  formSearchKeyword: string = '';
  formSearchResults: any[] = null;
  cfgFormPreviewSelectList: any[] = [];
  cfgFormPreviewSelectListIsLoading: boolean = false;

  selectedCfgForm: FormConfigurationModel = null;
  selectedCfgFormPreviewId: string = null;

  categoryEnums: EnumValidValueItemModel[] = [];
  customEnums: EnumValidValueItemModel[] = [];
  cfgFormDataSourceList: any[] = [];
  cfgFormVersionStatusList: any[] = [];

  guidList: string[] = [];
  formIsSaved: boolean = false;
  // True while a cached form hasn't been confirmed against the server yet. formIsSaved is false
  // then too, but the form is not known to be unsaved, so the Save Properties prompt is held back.
  formSaveStatusUnknown: boolean = false;
  // In-flight server check for a cached form. Its own subscription, not fetchCfgFormSubscription:
  // fetchCfgForm unsubscribes the previous call, which would leave this check pending forever.
  private formSaveCheck: Promise<'saved' | 'unsaved' | 'unknown'> | null = null;
  private formSaveCheckSubscription: Subscription;

  helpText = FORM_PROPERTY_HELP;

  // Used to reveal the "Save Properties" button when the user is told to press it — the panel
  // is collapsed by default, so the instruction is otherwise pointing at something off-screen.
  // Optional: the panel is inside an @if, so the query is genuinely unset until that subtree
  // renders — openPropertiesPanel() handles that case.
  @ViewChild('propertiesPanel') propertiesPanel?: MatExpansionPanel;

  // Valid values fetched lazily for the AI PDF import flow (the finder doesn't need them otherwise).
  private aiValidValues: any[] = null;

  // Multi-select for Print to PDF
  selectedFormsForPrint: Set<string> = new Set();
  isMultiSelectMode: boolean = false;

  // Set active version selection mode
  isSelectingActiveVersion: boolean = false;

  // Trash / Recently Deleted
  isTrashView: boolean = false;
  trashEntries: TrashEntry[] = [];
  deletedFormEntries: TrashEntry[] = [];
  deletedVersionsGrouped: { formName: string; formId: string; versions: TrashEntry[] }[] = [];
  selectedTrashEntry: TrashEntry = null;

  private searchFormSubscription: Subscription;
  private fetchCfgFormSubscription: Subscription;
  private trashSubscription: Subscription;

  private static readonly RETURN_URL_KEY = 'formBuilderReturnUrl';

  constructor(
    private location: Location,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private stateService: FormsBuilderStateService,
    private builderService: FormsBuilderPolishedService,
    private formsService: FormsService,
    public globals: FormsGlobals,
    private staticDataService: StaticDataService,
    private trashService: FormsBuilderTrashService,
    private aiDraftService: FormsBuilderAiDraftService,
    private readonly uiService: UiService
  ) {}

  goBack(): void {
    const returnUrl = sessionStorage.getItem(FormFinderComponent.RETURN_URL_KEY);
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.location.back();
    }
  }

  ngOnInit(): void {
    this.TDOCID = parseInt(localStorage.getItem('TDOCID') || '0', 10);

    const prevUrl = this.router.lastSuccessfulNavigation()
      ?.previousNavigation?.finalUrl?.toString();
    if (prevUrl && !prevUrl.startsWith('/forms/builder')) {
      sessionStorage.setItem(FormFinderComponent.RETURN_URL_KEY, prevUrl);
    }

    if (this.staticDataService.isReady) {
      this.setupEnums();
    } else {
      this.staticDataService.validValuesReadyEvent.subscribe({
        complete: () => {
          this.setupEnums();
        }
      });
    }

    // Load previously selected form from state if available (best-effort, may be stale)
    const existingForm = this.stateService.selectedForm;
    if (existingForm) {
      existingForm.cfgDFormVersions?.forEach(version => {
        version.cfgDFormParagraphs = (version.cfgDFormParagraphs || []).filter(
          paragraph => !paragraph.xauditDeleted
        );
      });
      this.selectedCfgForm = existingForm;
      this.selectedCfgFormPreviewId = existingForm.DFormId;
      // The cache is written when a form is selected — before any save — so a form whose save
      // failed (or was never attempted) is still in localStorage on the next visit, and the cache
      // alone can't be trusted. A form already confirmed this session (e.g. returning from the
      // editor after an import) is saved; anything else is "unknown" until the server answers,
      // which is not the same as unsaved — the user is not told to Save Properties meanwhile.
      if (this.stateService.isFormConfirmedSaved(existingForm.DFormId)) {
        this.setFormSaved(true);
      } else {
        this.formIsSaved = false;
        this.formSaveStatusUnknown = true;
      }

      // Reconcile with the server in the background in case the cache is stale
      // (e.g. a page reload landed between a save and the next cache write).
      this.checkCachedFormSaved(existingForm.DFormId);
    }

    this.trashSubscription = this.trashService.trash$.subscribe(entries => {
      this.trashEntries = entries;
      this.updateDerivedTrashLists();
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.searchFormSubscription) {
      this.searchFormSubscription.unsubscribe();
    }
    if (this.fetchCfgFormSubscription) {
      this.fetchCfgFormSubscription.unsubscribe();
    }
    if (this.formSaveCheckSubscription) {
      this.formSaveCheckSubscription.unsubscribe();
    }
    if (this.trashSubscription) {
      this.trashSubscription.unsubscribe();
    }
  }

  setupEnums(): void {
    this.categoryEnums = this.staticDataService.getValidValueItemsByValidValueName('enumFormCategory');
    this.customEnums = this.staticDataService.getValidValueItemsByValidValueName('enumCustomForm');
    this.setupStatusList();
    this.setupDataSources();
    this.init();
  }

  setupStatusList(): void {
    this.cfgFormVersionStatusList = [
      { name: 'Approved', id: this.globals.FORM_STATUS_APPROVED },
      { name: 'Draft', id: this.globals.FORM_STATUS_DRAFT },
      { name: 'Ready for review', id: this.globals.FORM_STATUS_READY_FOR_REVIEW },
      { name: 'In review', id: this.globals.FORM_STATUS_IN_REVIEW }
    ];
  }

  setupDataSources(): void {
    this.cfgFormDataSourceList = [
      { name: 'Generic', id: this.globals.FORM_DATA_SOURCE_GENERIC },
      { name: 'Custom', id: this.globals.FORM_DATA_SOURCE_CUSTOM },
    ];
  }

  init(): void {
    this.cfgFormPreviewSelectListIsLoading = true;
    this.fetchFormList()
      .then((results: any[]) => {
        this.cfgFormPreviewSelectList = results.sort((a, b) => {
          return a.DFormName.toLowerCase().localeCompare(b.DFormName.toLowerCase());
        });
        this.cfgFormPreviewSelectListIsLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
      })
      .catch(err => {
        this.cfgFormPreviewSelectListIsLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Error', 'Error loading form list');
      });
  }

  fetchFormList(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.builderService.getCfgFormSimpleList()
        .subscribe(
          response => resolve(response),
          err => reject(err)
        );
    });
  }

  clearSearch(): void {
    this.formSearchKeyword = '';
    this.searchForForm();
  }

  searchForForm(): void {
    const keyword = this.formSearchKeyword.trim();

    this.isSearchingForForms = true;
    if (this.searchFormSubscription) {
      this.searchFormSubscription.unsubscribe();
    }

    if (!keyword) {
      this.formSearchResults = null;
      this.isSearchingForForms = false;
      return;
    }

    this.searchFormSubscription = this.builderService.searchForFormByKeyword(keyword)
      .subscribe(
        response => {
          this.isSearchingForForms = false;
          this.formSearchResults = response.sort((a, b) => {
            const keywordIndexA = a.DFormName.toLowerCase().indexOf(keyword.toLowerCase());
            const keywordIndexB = b.DFormName.toLowerCase().indexOf(keyword.toLowerCase());
            return keywordIndexA - keywordIndexB;
          });
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
        },
        err => {
          this.isSearchingForForms = false;
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
          this.showMessage('Error', 'Error searching for form');
          console.error(err);
        }
      );
  }

  selectAndFetchCfgForm(selectedFormId: string): void {
    this.selectedCfgFormPreviewId = selectedFormId;
    this.selectedCfgForm = null;
    this.cfgFormIsLoading = true;

    this.fetchCfgForm(selectedFormId)
      .then((result: FormConfigurationModel) => {
        this.cfgFormIsLoading = false;
        this.selectedCfgForm = result;
        this.setFormSaved(true);
        this.selectedCfgForm.cfgDFormVersions = this.selectedCfgForm.cfgDFormVersions
          .filter(version => !version.xauditDeleted)
          .sort((a, b) => b.Version - a.Version); // Sort descending by version

        this.selectedCfgForm.cfgDFormVersions.forEach(version => {
          version.cfgDFormParagraphs = version.cfgDFormParagraphs.filter(
            paragraph => !paragraph.xauditDeleted
          );
        });

        // Save to state service
        this.stateService.setSelectedForm(this.selectedCfgForm);
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
      })
      .catch(err => {
        this.cfgFormIsLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Error', 'Error loading form');
      });
  }

  fetchCfgForm(formId: string): Promise<FormConfigurationModel> {
    return new Promise((resolve, reject) => {
      if (this.fetchCfgFormSubscription) {
        this.fetchCfgFormSubscription.unsubscribe();
      }

      this.fetchCfgFormSubscription = this.formsService.getCfgDForm(formId)
        .subscribe(
          response => {
            if (response) {
              resolve(response);
            } else {
              // 200 with a null body: the id isn't in the database.
              reject(FORM_NOT_FOUND);
            }
          },
          err => reject(err)
        );
    });
  }

  // Navigate directly to edit a version
  editVersion(version: FormVersionConfigurationModel): void {
    // Note: Revision will be incremented on first save in paragraph-builder, not here
    this.stateService.setSelectedForm(this.selectedCfgForm);
    this.stateService.setSelectedVersion(version);
    this.stateService.navigateToEditor(
      this.selectedCfgForm.DFormId,
      version.DFormVersionId,
      'edit'
    );
  }

  // View a version (read-only mode)
  viewVersion(version: FormVersionConfigurationModel): void {
    this.stateService.setSelectedForm(this.selectedCfgForm);
    this.stateService.setSelectedVersion(version);
    this.stateService.navigateToEditor(
      this.selectedCfgForm.DFormId,
      version.DFormVersionId,
      'view'
    );
  }

  createNewFormButtonPressed(): void {
    const dialogRef = this.dialog.open(FormsBuilderPolishedFormCreationComponent, {
      width: '500px',
      height: '450px',
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.proceed) {
        this.isLoading = true;
        this.formSearchResults = null;
        this.formSearchKeyword = '';

        if (result.mode === 'duplicate') {
          this.handleDuplicateForm(result);
        } else {
          this.handleCreateForm(result);
        }
      }
    });
  }

  private handleCreateForm(result: any): void {
    this.createAndSelectNewCfgForm(result.DFormName)
      .then(() => {
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        // A brand-new form isn't persisted until Save Properties is pressed, and the panel holding
        // that button is collapsed by default — open it up front rather than waiting for the user
        // to hunt for it (or to click New Version and be told).
        this.openPropertiesPanel();
        this.showMessage('Success', 'Form created. Save properties to continue.');
      })
      .catch(err => {
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Error', 'Error creating form');
      });
  }

  private handleDuplicateForm(result: any): void {
    this.fetchCfgForm(result.selectedForm.DFormId)
      .then((formToClone: FormConfigurationModel) => {
        return this.duplicateAForm(formToClone, result.DFormName);
      })
      .then((clonedForm: FormConfigurationModel) => {
        return this.saveDuplicatedForm(clonedForm);
      })
      .then((savedForm: FormConfigurationModel) => {
        this.selectedCfgForm = savedForm;
        this.setFormSaved(true);
        this.stateService.setSelectedForm(savedForm);
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Success', 'Form duplicated');
      })
      .catch(err => {
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Error', 'Error duplicating form');
        console.error(err);
      });
  }

  createAndSelectNewCfgForm(newName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.selectedCfgForm = null;
      this.selectedCfgFormPreviewId = null;

      const newForm = new FormConfigurationModel();
      newForm.cfgDFormVersions = [];
      newForm.TDOCID = this.TDOCID;
      newForm.DFormName = newName;

      this.getNewGuids(1)
        .then((newGuid: string[]) => {
          newForm.DFormId = newGuid[0];
          newForm.xauditDeleted = false;
          this.selectedCfgForm = newForm;
          this.selectedCfgFormPreviewId = newForm.DFormId;
          this.setFormSaved(false);
          this.stateService.setSelectedForm(this.selectedCfgForm);
          resolve();
        })
        .catch(err => {
          this.showMessage('Error', 'Error creating form');
          reject(err);
        });
    });
  }

  createNewCfgFormVersion(): Promise<void> {
    // Fetch from backend to include soft-deleted versions in the max calculation,
    // preventing duplicate version numbers after a delete-then-create cycle.
    //
    // Goes through fetchCfgForm rather than calling getCfgDForm directly: the endpoint answers
    // 200-with-a-null-body for an unknown DFormId, and reading .cfgDFormVersions off that null
    // inside a subscribe callback throws where RxJS sends it to the global error handler instead
    // of the adjacent error callback — leaving this promise permanently pending and the caller's
    // spinner up forever. fetchCfgForm rejects with FORM_NOT_FOUND instead.
    return this.fetchCfgForm(this.selectedCfgForm.DFormId)
      .then((freshForm: FormConfigurationModel) => {
        let versionNumber = 1;
        (freshForm.cfgDFormVersions || []).forEach(version => {
          if (version.Version >= versionNumber) {
            versionNumber = version.Version + 1;
          }
        });

        return this.getNewGuids(1).then((newGuid: string[]) => {
          const newVersion = new FormVersionConfigurationModel();
          newVersion.DFormId = this.selectedCfgForm.DFormId;
          newVersion.DFormVersionId = newGuid[0];
          newVersion.Version = versionNumber;
          newVersion.Revision = 1;
          newVersion.xauditDeleted = false;
          newVersion.enumDFormBuildStatusID = this.globals.FORM_STATUS_DRAFT;
          newVersion.cfgDFormParagraphs = [];

          this.selectedCfgForm.cfgDFormVersions = this.selectedCfgForm.cfgDFormVersions || [];
          this.selectedCfgForm.cfgDFormVersions.push(newVersion);
          this.stateService.setSelectedForm(this.selectedCfgForm);
        });
      });
  }

  createNewVersionForCurrentForm(): void {
    if (!this.selectedCfgForm) {
      this.showMessage('Error', 'Please select a form first');
      return;
    }

    if (!this.formIsSaved) {
      this.whenFormSaveStatusKnown(() => this.createNewVersionForCurrentForm());
      return;
    }

    this.isLoading = true;
    let newVersionId: string | null = null;
    this.createNewCfgFormVersion()
      .then(() => {
        const newVersion = this.selectedCfgForm.cfgDFormVersions[
          this.selectedCfgForm.cfgDFormVersions.length - 1
        ];
        newVersionId = newVersion.DFormVersionId;
        return this.saveVersion(newVersion);
      })
      .then(() => {
        newVersionId = null;
        this.selectedCfgForm.cfgDFormVersions.sort((a, b) => b.Version - a.Version);
        this.stateService.setSelectedForm(this.selectedCfgForm);
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Success', 'New version created');
      })
      .catch(err => {
        if (newVersionId != null) {
          const idx = this.selectedCfgForm.cfgDFormVersions.findIndex(
            v => v.DFormVersionId === newVersionId
          );
          if (idx >= 0) {
            this.selectedCfgForm.cfgDFormVersions.splice(idx, 1);
          }
          this.stateService.setSelectedForm(this.selectedCfgForm);
          newVersionId = null;
        }
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        // The form vanished from under us (or was never persisted despite formIsSaved being set):
        // correct the flag so the buttons re-disable, and say what to do about it.
        if (err === FORM_NOT_FOUND) {
          this.setFormSaved(false);
          this.showUnsavedFormGuidance();
        } else {
          this.showMessage('Error', 'Error creating version');
        }
      });
  }

  /**
   * Explains that the selected form exists only in the browser and names the next action.
   * Reached both from the disabled-button guard and from a create that discovered mid-flight that
   * the form has no backing row. Expands the Form Properties panel so "Save Properties" — the
   * button being described, and the thing that actually creates the form — is visible.
   */
  private showUnsavedFormGuidance(): void {
    const blank = this.missingRequiredProperties();

    const message = blank.length
      ? `This form hasn't been saved yet. Open Form Properties, set the ${blank.join(' and ')}, `
        + 'then press Save Properties.'
      : `This form hasn't been saved yet. Open Form Properties and press Save Properties to continue.`;

    this.openPropertiesPanel();
    this.showMessage('Warning', message, 8000);
  }

  /**
   * Properties treated as required before a form can be saved, returned by display name so
   * prompts and tooltips can say which ones are outstanding.
   *
   * The API does not enforce either (saveCfgFormAuto accepts both as null), but a form without
   * them is unusable — Data Source in particular decides whether the form supports versions at
   * all — so the requirement is applied here in the UI.
   */
  private missingRequiredProperties(): string[] {
    const missing: string[] = [];
    if (!this.selectedCfgForm?.enumFormCategoryID) { missing.push('Category'); }
    if (!this.selectedCfgForm?.enumFormDataSourceID) { missing.push('Data Source'); }
    return missing;
  }

  get canSaveFormProperties(): boolean {
    return this.missingRequiredProperties().length === 0;
  }

  /** Tooltip for the version buttons while the form isn't confirmed saved; empty once it is. */
  get unsavedFormTooltip(): string {
    if (this.formIsSaved) { return ''; }
    return this.formSaveStatusUnknown ? 'Checking that this form is saved…' : 'Save form properties first';
  }

  private setFormSaved(saved: boolean): void {
    this.formIsSaved = saved;
    this.formSaveStatusUnknown = false;
    this.stateService.setFormConfirmedSaved(this.selectedCfgForm?.DFormId, saved);
  }

  private currentFormSaveStatus(): 'saved' | 'unsaved' | 'unknown' {
    if (this.formIsSaved) { return 'saved'; }
    return this.formSaveStatusUnknown ? 'unknown' : 'unsaved';
  }

  /**
   * Confirms a cached form against the server and refreshes it from the response. Concurrent
   * callers share the one in-flight request. A null body means the form isn't in the database
   * (unsaved); a failed request says nothing either way, so the status stays "unknown" and the
   * next click checks again rather than telling the user to save a form that may well exist.
   */
  private checkCachedFormSaved(formId: string): Promise<'saved' | 'unsaved' | 'unknown'> {
    if (this.formSaveCheck) { return this.formSaveCheck; }

    this.formSaveCheck = new Promise(resolve => {
      this.formSaveCheckSubscription = this.formsService.getCfgDForm(formId).subscribe(
        (result: FormConfigurationModel) => {
          this.formSaveCheck = null;
          // The user picked another form meanwhile; its own load has already set the status.
          if (this.selectedCfgForm?.DFormId !== formId) {
            resolve(this.currentFormSaveStatus());
            return;
          }

          if (!result) {
            console.error(`Cached form ${formId} is not on the server; treating it as unsaved`);
            this.setFormSaved(false);
          } else {
            result.cfgDFormVersions = (result.cfgDFormVersions || [])
              .filter(version => !version.xauditDeleted)
              .sort((a, b) => b.Version - a.Version);
            result.cfgDFormVersions.forEach(version => {
              version.cfgDFormParagraphs = (version.cfgDFormParagraphs || []).filter(
                paragraph => !paragraph.xauditDeleted
              );
            });
            this.selectedCfgForm = result;
            this.setFormSaved(true);
            this.stateService.setSelectedForm(result);
          }
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
          resolve(this.currentFormSaveStatus());
        },
        err => {
          this.formSaveCheck = null;
          console.error(err);
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
          resolve(this.currentFormSaveStatus());
        }
      );
    });
    return this.formSaveCheck;
  }

  /**
   * Guard for the version actions when the form isn't confirmed saved. Known-unsaved explains
   * what to do; still-checking waits for the server and re-runs `action`, so a click made before
   * the background check lands isn't answered with a false "save your properties" prompt.
   */
  private whenFormSaveStatusKnown(action: () => void): void {
    if (!this.formSaveStatusUnknown) {
      this.showUnsavedFormGuidance();
      return;
    }

    this.isLoading = true;
    if (!this.isDestroyed) { this.cdr.detectChanges(); }
    this.checkCachedFormSaved(this.selectedCfgForm.DFormId).then(status => {
      this.isLoading = false;
      if (!this.isDestroyed) { this.cdr.detectChanges(); }
      if (status === 'saved') {
        action();
      } else if (status === 'unsaved') {
        this.showUnsavedFormGuidance();
      } else {
        this.showMessage('Error', 'Couldn\'t confirm this form is saved. Check your connection and try again.');
      }
    });
  }

  get savePropertiesTooltip(): string {
    const missing = this.missingRequiredProperties();
    return missing.length ? `Select a ${missing.join(' and ')} to save` : '';
  }

  /**
   * Expands the Form Properties panel. The panel is inside an @if on selectedCfgForm, so when the
   * form was only just created the subtree may not exist and the view query is still unset.
   *
   * Order matters in the fallback: flush change detection FIRST so the subtree is created and the
   * query populated, then open, then flush again so the expanded state renders. Opening before that
   * first flush would no-op against an undefined panel with nothing left to retry.
   */
  private openPropertiesPanel(): void {
    if (this.propertiesPanel) {
      this.propertiesPanel.open();
      return;
    }

    Promise.resolve().then(() => {
      if (this.isDestroyed) { return; }
      this.cdr.detectChanges();
      this.propertiesPanel?.open();
      this.cdr.detectChanges();
    });
  }

  /**
   * "Import PDF" next to "New Version": opens the AI Form Studio in PDF mode against a stub,
   * unpersisted version. If the user accepts the import, a new draft version containing the
   * imported paragraphs is created, saved, and opened in the editor — all automatically.
   * Cancelling the dialog leaves nothing behind (no version is created until accept).
   */
  importPdfAsNewVersion(): void {
    if (!this.selectedCfgForm) {
      this.showMessage('Error', 'Please select a form first');
      return;
    }

    if (!this.formIsSaved) {
      this.whenFormSaveStatusKnown(() => this.importPdfAsNewVersion());
      return;
    }

    this.isLoading = true;
    Promise.all([this.ensureValidValues(), this.getNewGuids(1)])
      .then(([validValues, guids]: [any[], string[]]) => {
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.openPdfImportStudio(guids[0], validValues);
      })
      .catch(err => {
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Error', 'Error loading form configurations');
        console.error(err);
      });
  }

  private ensureValidValues(): Promise<any[]> {
    if (this.aiValidValues) {
      return Promise.resolve(this.aiValidValues);
    }
    return new Promise((resolve, reject) => {
      this.builderService.getAllValidValues().subscribe(
        response => {
          this.aiValidValues = (response || []).sort((a: any, b: any) =>
            a.ValidValueName.toLowerCase().localeCompare(b.ValidValueName.toLowerCase())
          );
          resolve(this.aiValidValues);
        },
        err => reject(err)
      );
    });
  }

  private openPdfImportStudio(newVersionGuid: string, validValues: any[]): void {
    // Provisional next version number for display — recomputed from the backend on accept.
    let provisionalVersion = 1;
    (this.selectedCfgForm.cfgDFormVersions || []).forEach(v => {
      if (v.Version >= provisionalVersion) {
        provisionalVersion = v.Version + 1;
      }
    });

    // Stub base version carrying a real server GUID, so paragraphs created inside the dialog
    // already reference their final version id. Nothing is persisted unless the user accepts.
    const stubVersion = new FormVersionConfigurationModel();
    stubVersion.DFormVersionId = newVersionGuid;
    stubVersion.DFormId = this.selectedCfgForm.DFormId;
    stubVersion.Version = provisionalVersion;
    stubVersion.Revision = 1;
    stubVersion.enumDFormBuildStatusID = this.globals.FORM_STATUS_DRAFT;
    stubVersion.xauditDeleted = false;
    stubVersion.cfgDFormParagraphs = [];

    const templateGroups = [
      ...this.aiDraftService.prebuiltTemplateGroups,
      ...this.aiDraftService.loadCustomTemplateGroups()
    ];
    const draftContext: AiDraftApplyContext = {
      validValues,
      templateFields: this.aiDraftService.flattenTemplateFields(templateGroups),
      getGuids: (n: number) => this.getNewGuids(n),
      isPreview: true
    };

    const dialogRef = this.dialog.open(FormsBuilderAiStudioDialogComponent, {
      width: '95vw',
      maxWidth: '98vw',
      height: '92vh',
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'ai-studio-dialog-panel',
      data: {
        entry: 'pdf',
        autoVersion: true,
        autoVersionLabel: `v${provisionalVersion}.1`,
        catalog: this.aiDraftService.buildCatalogSnapshot(templateGroups, validValues),
        existingFormContext: {
          formName: this.selectedCfgForm.DFormName,
          version: provisionalVersion,
          revision: 1
        },
        baseVersion: stubVersion,
        applyDraftToWorkingVersion: (
          base: FormVersionConfigurationModel,
          draft: AiGeneratedFormDraft,
          mode: 'pdf' | 'edit'
        ) => this.aiDraftService.applyDraftToWorkingVersion(base, draft, mode, draftContext),
        snapshotParagraphs: (version: FormVersionConfigurationModel) =>
          this.aiDraftService.buildExistingParagraphSnapshot(version)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.applied || !result?.workingVersion) {
        return; // Cancelled — nothing was created or saved.
      }
      this.finalizeImportedVersion(result.workingVersion as FormVersionConfigurationModel, stubVersion);
    });
  }

  /**
   * Accept path of the PDF import: computes the authoritative next version number from a fresh
   * backend fetch, assembles the new draft version with the imported paragraphs, persists the
   * whole tree in one atomic call, then opens it in the editor. Nothing exists on the backend
   * until the save succeeds, so a failure leaves no orphan version.
   */
  private finalizeImportedVersion(
    workingVersion: FormVersionConfigurationModel,
    stubVersion: FormVersionConfigurationModel
  ): void {
    this.isLoading = true;

    // Fresh fetch includes soft-deleted versions in the max calculation (same rule as
    // createNewCfgFormVersion) and catches versions created elsewhere while the dialog was open.
    this.fetchCfgForm(this.selectedCfgForm.DFormId)
      .then((freshForm: FormConfigurationModel) => {
        let versionNumber = 1;
        (freshForm.cfgDFormVersions || []).forEach(version => {
          if (version.Version >= versionNumber) {
            versionNumber = version.Version + 1;
          }
        });

        const newVersion = this.buildImportedVersionForSave(workingVersion, stubVersion, versionNumber);
        return this.saveDuplicatedVersion(newVersion)
          .then((savedVersion: any) => ({ savedVersion, versionNumber }));
      })
      .then(({ savedVersion, versionNumber }) => {
        const savedVersionId = savedVersion?.DFormVersionId || stubVersion.DFormVersionId;
        this.openSavedImportedVersion(savedVersionId, versionNumber, savedVersion);
      })
      .catch(err => {
        console.error('PDF import: failed to create the new version', err);
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        // Keep the accepted AI content alive: offer a retry instead of silently dropping it.
        this.confirm('Creating the new version failed. Try again? (Choosing No discards the imported content.)')
          .then(retry => {
            if (retry) {
              this.finalizeImportedVersion(workingVersion, stubVersion);
            } else {
              // 'Cancelled', not 'Error' — the user was asked whether to retry and chose not to.
              // Labelling their own decision as a failure reads as though something went wrong on
              // top of the save that already failed.
              this.showMessage('Error', 'Import cancelled — no version was created');
            }
          });
      });
  }

  /**
   * Assembles the save payload for the imported version: non-deleted paragraphs/items from the
   * dialog's working copy (soft-deleted rows were never persisted, so they are dropped), with
   * navigation properties stripped the same way duplicateAVersion does — EF treats non-null nav
   * objects as INSERT targets. Item and paragraph GUIDs are already final server GUIDs, so
   * ParentDFormItemId links are kept as-is (no remapping, unlike duplication).
   */
  private buildImportedVersionForSave(
    workingVersion: FormVersionConfigurationModel,
    stubVersion: FormVersionConfigurationModel,
    versionNumber: number
  ): FormVersionConfigurationModel {
    const newVersion: FormVersionConfigurationModel = JSON.parse(JSON.stringify(stubVersion));
    newVersion.Version = versionNumber;
    newVersion.Revision = 1;
    newVersion.enumDFormBuildStatusID = this.globals.FORM_STATUS_DRAFT;
    newVersion.enumDFormBuildStatus = null;
    newVersion.xauditDeleted = false;

    newVersion.cfgDFormParagraphs = (workingVersion.cfgDFormParagraphs || [])
      .filter(p => !p.xauditDeleted)
      .map(originalParagraph => {
        const paragraph = JSON.parse(JSON.stringify(originalParagraph));
        paragraph.DFormVersionId = newVersion.DFormVersionId;
        paragraph.enumParagraphOrientation = null;
        paragraph.enumItemLayout = null;
        paragraph.RepeatDFormItem = null;

        paragraph.cfgDFormItems = (paragraph.cfgDFormItems || [])
          .filter((item: any) => !item.xauditDeleted)
          .map((item: any) => {
            // Keep ValidValueId and ParentDFormItemId — only navigation objects are cleared.
            item.ValidValue = null;
            item.cfgDFormMasterItem = null;
            item.DFormMasterItemId = null; // backend creates master items on save
            item.Calculation = null;
            item.BeforeDisplayItemAction = null;
            item.AfterUpdateItemAction = null;
            item.ChildDFormItems = [];
            item.enumFormControl = null;
            item.enumLabelAligment = null;
            item.enumPostLabelAligment = null;
            return item;
          });

        return paragraph;
      });

    return newVersion;
  }

  /**
   * Post-save: refresh the form (same post-processing as selectAndFetchCfgForm), select the new
   * version, and open it in the editor. The save already succeeded, so a refresh failure must
   * not strand the user on the finder — it falls back to navigating with local objects (the
   * editor reloads from the API if state is stale).
   */
  private openSavedImportedVersion(savedVersionId: string, versionNumber: number, savedVersion: any): void {
    // The import persisted the version without master items (only the editor's save creates
    // them), so the editor raises its save banner when it opens this version.
    this.stateService.setPendingImportSave(savedVersionId);
    this.fetchCfgForm(this.selectedCfgForm.DFormId)
      .then((freshForm: FormConfigurationModel) => {
        freshForm.cfgDFormVersions = (freshForm.cfgDFormVersions || [])
          .filter(version => !version.xauditDeleted)
          .sort((a, b) => b.Version - a.Version);
        freshForm.cfgDFormVersions.forEach(version => {
          version.cfgDFormParagraphs = (version.cfgDFormParagraphs || []).filter(
            paragraph => !paragraph.xauditDeleted
          );
        });

        const newVersion = freshForm.cfgDFormVersions.find(v => v.DFormVersionId === savedVersionId)
          || freshForm.cfgDFormVersions.find(v => v.Version === versionNumber);

        this.selectedCfgForm = freshForm;
        this.selectedCfgFormPreviewId = freshForm.DFormId;
        this.setFormSaved(true);
        this.stateService.setSelectedForm(freshForm);
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }

        if (!newVersion) {
          this.showMessage('Success', 'New version created from document import');
          return;
        }

        this.showMessage('Success', `New version v${newVersion.Version}.${newVersion.Revision} created from document import`);
        this.openAiImportReviewDialog(this.aiDraftService.versionHasNoteEnabledItems(newVersion))
          .then(() => this.editVersion(newVersion));
      })
      .catch(err => {
        console.error('PDF import: version saved but form refresh failed', err);
        if (savedVersion && this.selectedCfgForm?.cfgDFormVersions) {
          this.selectedCfgForm.cfgDFormVersions.unshift(savedVersion);
          this.stateService.setSelectedForm(this.selectedCfgForm);
        }
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Success', 'New version created from document import');
        this.openAiImportReviewDialog(this.aiDraftService.versionHasNoteEnabledItems(savedVersion))
          .then(() => this.stateService.navigateToEditor(this.selectedCfgForm.DFormId, savedVersionId, 'edit'));
      });
  }

  /**
   * Must-acknowledge popup shown right after an AI PDF import completes, reminding the user to
   * review the AI-generated form (and its Note Builder logic, when present) before relying on it.
   */
  private openAiImportReviewDialog(hasNoteBuilderItems: boolean): Promise<void> {
    return new Promise(resolve => {
      const dialogRef = this.dialog.open(FormsBuilderAiImportReviewDialogComponent, {
        width: '520px',
        disableClose: true,
        data: { hasNoteBuilderItems }
      });
      dialogRef.afterClosed().subscribe(() => resolve());
    });
  }

  duplicateVersion(version: FormVersionConfigurationModel): void {
    if (!this.selectedCfgForm) return;

    this.isLoading = true;
    const newVersion = new FormVersionConfigurationModel();
    newVersion.DFormId = this.selectedCfgForm.DFormId;

    Promise.all([
      this.duplicateAVersion(version, newVersion),
      new Promise<FormConfigurationModel>((res, rej) =>
        this.formsService.getCfgDForm(this.selectedCfgForm.DFormId).subscribe(res, rej)
      )
    ])
      .then(([clonedVersion, freshForm]: [FormVersionConfigurationModel, FormConfigurationModel]) => {
        let maxVersion = 0;
        (freshForm.cfgDFormVersions || []).forEach(v => {
          if (v.Version > maxVersion) {
            maxVersion = v.Version;
          }
        });
        clonedVersion.Version = maxVersion + 1;
        return this.saveDuplicatedVersion(clonedVersion);
      })
      .then((savedVersion: any) => {
        // API returns the saved version - add it to the form's version list
        this.selectedCfgForm.cfgDFormVersions.unshift(savedVersion);
        this.stateService.setSelectedForm(this.selectedCfgForm);
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Success', 'Version duplicated');
      })
      .catch(err => {
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.showMessage('Error', 'Error duplicating version');
        console.error(err);
      });
  }

  deleteVersion(version: FormVersionConfigurationModel): void {
    const activeVersionNumber = this.getActiveVersionNumber();
    const isActive =
      version.enumDFormBuildStatusID?.toLowerCase() === this.globals.FORM_STATUS_APPROVED?.toLowerCase() &&
      version.Version === activeVersionNumber;

    if (isActive) {
      this.showMessage('Warning', 'Cannot delete the active version. Set another version as active first.');
      return;
    }

    this.confirm('Are you sure you want to delete this version?')
      .then(result => {
        if (!result) return;

        version.xauditDeleted = true;
        this.saveVersion(version)
          .then(() => {
            const index = this.selectedCfgForm.cfgDFormVersions.findIndex(
              v => v.DFormVersionId === version.DFormVersionId
            );
            if (index >= 0) {
              this.selectedCfgForm.cfgDFormVersions.splice(index, 1);
            }
            this.trashService.addDeletedVersion(
              version.DFormVersionId,
              `v${version.Version}.${version.Revision}`,
              this.selectedCfgForm.DFormId,
              this.selectedCfgForm.DFormName
            );
            this.stateService.setSelectedForm(this.selectedCfgForm);
            if (!this.isDestroyed) { this.cdr.detectChanges(); }
            this.showMessage('Success', 'Version moved to Recently Deleted');
          })
          .catch(err => {
            version.xauditDeleted = false;
            this.showMessage('Error', 'Error deleting version');
          });
      });
  }

  private saveFormToBackend(): Promise<void> {
    return new Promise((resolve, reject) => {
      const formClone = JSON.parse(JSON.stringify(this.selectedCfgForm));
      formClone.cfgDFormVersions = [];
      formClone.enumFormCategory = null;
      formClone.enumFormDataSource = null;
      formClone.enumCustomForm = null;

      this.builderService.saveCfgDForm(formClone).subscribe(
        (savedForm: FormConfigurationModel) => {
          // Sync backend-assigned DFormId (backend replaces client GUID on INSERT)
          if (savedForm && savedForm.DFormId !== this.selectedCfgForm.DFormId) {
            const oldId = this.selectedCfgForm.DFormId;
            this.selectedCfgForm.DFormId = savedForm.DFormId;
            this.selectedCfgForm.cfgDFormVersions.forEach(v => {
              if (v.DFormId === oldId) { v.DFormId = savedForm.DFormId; }
            });
          }
          this.setFormSaved(true);
          // Update form list
          const listItem = this.cfgFormPreviewSelectList.find(
            f => f.DFormId === this.selectedCfgForm.DFormId
          );
          if (listItem) {
            listItem.DFormName = this.selectedCfgForm.DFormName;
          } else {
            this.cfgFormPreviewSelectList.push({
              DFormId: this.selectedCfgForm.DFormId,
              DFormName: this.selectedCfgForm.DFormName
            });
            this.cfgFormPreviewSelectList.sort((a, b) =>
              a.DFormName.toLowerCase().localeCompare(b.DFormName.toLowerCase())
            );
          }
          resolve();
        },
        err => reject(err)
      );
    });
  }

  saveFormProperties(): void {
    if (!this.selectedCfgForm || this.isSaving) return;

    // Backstop for the disabled button, so a programmatic caller can't persist an incomplete form.
    const missing = this.missingRequiredProperties();
    if (missing.length) {
      this.showMessage('Warning', `Select a ${missing.join(' and ')} before saving.`, 6000);
      return;
    }

    this.isSaving = true;
    this.isLoading = true;
    this.saveFormToBackend()
      .then(() => {
        this.isSaving = false;
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        this.stateService.setSelectedForm(this.selectedCfgForm);
        this.showMessage('Success', 'Form properties saved');
      })
      .catch(err => {
        this.isSaving = false;
        this.isLoading = false;
        if (!this.isDestroyed) { this.cdr.detectChanges(); }
        if (err.error?.Message === 'A custom form of this type already exists.') {
          this.showMessage('Error', err.error.Message);
        } else {
          this.showMessage('Error', 'Error saving form properties');
        }
      });
  }

  deleteForm(): void {
    if (!this.selectedCfgForm) return;

    this.confirm('Are you sure you want to delete this form?')
      .then(result => {
        if (!result) return;

        this.builderService.deleteCfgForm(this.selectedCfgForm.DFormId)
          .subscribe(
            res => {
              if (res.IsProcedureForms) {
                const config = new MatDialogConfig();
                config.panelClass = 'dialog';
                config.data = res;
                this.dialog.open(ProcedureFormMismatchDialogComponent, config);
              }

              // Record in trash before clearing state
              this.trashService.addDeletedForm(
                this.selectedCfgForm.DFormId,
                this.selectedCfgForm.DFormName
              );
              if (this.selectedCfgForm.cfgDFormVersions) {
                this.selectedCfgForm.cfgDFormVersions.forEach(v => {
                  this.trashService.addDeletedVersion(
                    v.DFormVersionId,
                    `v${v.Version}.${v.Revision}`,
                    this.selectedCfgForm.DFormId,
                    this.selectedCfgForm.DFormName
                  );
                });
              }

              this.cfgFormPreviewSelectList = this.cfgFormPreviewSelectList.filter(
                f => f.DFormId.toLowerCase() !== this.selectedCfgForm.DFormId.toLowerCase()
              );

              this.formSearchResults = null;
              this.formSearchKeyword = '';
              this.selectedCfgForm = null;
              this.selectedCfgFormPreviewId = null;
              this.stateService.clearState();
              this.showMessage('Success', 'Form moved to Recently Deleted');
            },
            err => {
              this.showMessage('Error', 'Error deleting form');
            }
          );
      });
  }

  // Multi-select methods for Print to PDF
  toggleMultiSelectMode(): void {
    this.isMultiSelectMode = !this.isMultiSelectMode;
    if (!this.isMultiSelectMode) {
      this.selectedFormsForPrint.clear();
    }
  }

  toggleFormSelection(formId: string): void {
    if (this.selectedFormsForPrint.has(formId)) {
      this.selectedFormsForPrint.delete(formId);
    } else {
      this.selectedFormsForPrint.add(formId);
    }
  }

  isFormSelected(formId: string): boolean {
    return this.selectedFormsForPrint.has(formId);
  }

  selectAllForms(): void {
    const list = this.formSearchResults || this.cfgFormPreviewSelectList;
    list.forEach(form => this.selectedFormsForPrint.add(form.DFormId));
  }

  deselectAllForms(): void {
    this.selectedFormsForPrint.clear();
  }

  openPrintPreview(): void {
    if (this.selectedFormsForPrint.size === 0) {
      this.showMessage('Warning', 'Please select at least one form');
      return;
    }

    const selectedFormIds = Array.from(this.selectedFormsForPrint);
    this.dialog.open(PrintPreviewComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      panelClass: 'print-preview-dialog',
      data: { formIds: selectedFormIds }
    });
  }

  openTutorial(): void {
    this.dialog.open(FormsBuilderTutorialComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      panelClass: 'tutorial-dialog',
      data: { startSection: 'finder' }
    });
  }

  // Helper methods
  getNewGuids(count: number): Promise<string[]> {
    return new Promise((resolve, reject) => {
      if (this.guidList.length >= count) {
        resolve(this.guidList.splice(0, count));
      } else {
        this.builderService.getNewGuids(500)
          .subscribe(
            response => {
              this.guidList = this.guidList.concat(response);
              resolve(this.guidList.splice(0, count));
            },
            err => reject(err)
          );
      }
    });
  }

  confirm(message: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const dialogRef = this.dialog.open(FormsBuilderPolishedConfirmComponent, {
        width: '500px',
        height: '225px',
        data: { message }
      });
      dialogRef.afterClosed().subscribe(
        result => resolve(result),
        err => reject(err)
      );
    });
  }

  showMessage(type: string, message: string, duration: number = 3000): void {
    this.uiService.displayMessage(type, message);
  }

  getVersionLastEdited(version: FormVersionConfigurationModel): Date | null {
    const raw = version?.xauditModifiedOn ?? this.selectedCfgForm?.xauditModifiedOn ?? null;
    if (!raw) return null;
    if (raw instanceof Date) return raw;
    const str = raw as unknown as string;
    return new Date(str.endsWith('Z') ? str : str + 'Z');
  }

  getCategoryValue(enumFormCategoryID: string): EnumValidValueItemModel | null {
    return this.categoryEnums?.find(p => p.ValidValueItemId === enumFormCategoryID) || null;
  }

  getCustomValue(enumCustomFormID: string): EnumValidValueItemModel | null {
    return this.customEnums?.find(p => p.ValidValueItemId === enumCustomFormID) || null;
  }

  getDataSourceValue(enumDataSourceID: string): any {
    return this.cfgFormDataSourceList?.find(p => p.id === enumDataSourceID) || null;
  }

  getVersionStatusName(statusId: string, version: FormVersionConfigurationModel): string {
    // Find the highest approved version number
    const activeVersionNumber = this.getActiveVersionNumber();

    // Check if this version is approved and is the active one
    if (statusId?.toLowerCase() === this.globals.FORM_STATUS_APPROVED?.toLowerCase()) {
      if (activeVersionNumber !== null && version.Version === activeVersionNumber) {
        return 'Active';
      } else {
        return 'Inactive';
      }
    }

    if (statusId?.toLowerCase() === this.globals.FORM_STATUS_READY_FOR_REVIEW?.toLowerCase()) {
      return 'Ready for Review';
    }
    if (statusId?.toLowerCase() === this.globals.FORM_STATUS_IN_REVIEW?.toLowerCase()) {
      return 'In Review';
    }
    if (statusId?.toLowerCase() === this.globals.FORM_STATUS_DRAFT?.toLowerCase()) {
      return 'Draft';
    }

    return 'Unknown';
  }

  getVersionStatusClass(statusId: string, version: FormVersionConfigurationModel): string {
    return this.getVersionStatusName(statusId, version)?.toLowerCase().replace(/ /g, '-') ?? '';
  }

  getActiveVersionNumber(): number | null {
    if (!this.selectedCfgForm?.cfgDFormVersions) return null;

    const approvedVersions = this.selectedCfgForm.cfgDFormVersions
      .filter(v => v.enumDFormBuildStatusID?.toLowerCase() === this.globals.FORM_STATUS_APPROVED?.toLowerCase());

    if (approvedVersions.length === 0) return null;

    // Return the highest version number among approved versions
    return Math.max(...approvedVersions.map(v => v.Version));
  }

  // Duplication methods
  duplicateAForm(originalForm: FormConfigurationModel, newName: string): Promise<FormConfigurationModel> {
    return new Promise((resolve, reject) => {
      const newForm = new FormConfigurationModel();
      newForm.TDOCID = originalForm.TDOCID;
      newForm.DFormName = newName;
      newForm.enumFormCategoryID = originalForm.enumFormCategoryID;
      newForm.enumFormDataSourceID = originalForm.enumFormDataSourceID;
      newForm.enumCustomFormID = originalForm.enumCustomFormID;
      newForm.xauditDeleted = false;

      this.getNewGuids(1)
        .then((newGuid: string[]) => {
          newForm.DFormId = newGuid[0];
          return this.duplicateAllVersionsForForm(originalForm, newForm.DFormId);
        })
        .then((versions: FormVersionConfigurationModel[]) => {
          newForm.cfgDFormVersions = versions;
          resolve(newForm);
        })
        .catch(err => reject(err));
    });
  }

  duplicateAllVersionsForForm(originalForm: FormConfigurationModel, newFormId: string): Promise<FormVersionConfigurationModel[]> {
    const promises = originalForm.cfgDFormVersions.map(version => {
      const newVersion = new FormVersionConfigurationModel();
      newVersion.DFormId = newFormId;
      return this.duplicateAVersion(version, newVersion);
    });
    return Promise.all(promises);
  }

  duplicateAVersion(original: FormVersionConfigurationModel, newVersion: FormVersionConfigurationModel): Promise<FormVersionConfigurationModel> {
    return new Promise((resolve, reject) => {
      newVersion.Version = original.Version;
      newVersion.Revision = original.Revision;
      newVersion.enumDFormBuildStatusID = this.globals.FORM_STATUS_DRAFT;
      newVersion.xauditDeleted = false;

      const paragraphs = original.cfgDFormParagraphs || [];
      // Calculate total GUIDs needed: 1 for version + paragraphs + all items
      let totalItemCount = 0;
      paragraphs.forEach(p => {
        totalItemCount += (p.cfgDFormItems?.length || 0);
      });
      const totalGuidsNeeded = 1 + paragraphs.length + totalItemCount;

      this.getNewGuids(totalGuidsNeeded)
        .then((newGuids: string[]) => {
          let guidIndex = 0;
          newVersion.DFormVersionId = newGuids[guidIndex++];

          // Deep clone and regenerate IDs for paragraphs and items
          newVersion.cfgDFormParagraphs = paragraphs.map(originalParagraph => {
            const newParagraph = JSON.parse(JSON.stringify(originalParagraph));
            newParagraph.DFormParagraphId = newGuids[guidIndex++];
            newParagraph.DFormVersionId = newVersion.DFormVersionId;

            // Regenerate item IDs, tracking oldâ†’new GUID mapping for FK fixups
            const itemIdMap = new Map<string, string>();
            if (newParagraph.cfgDFormItems) {
              newParagraph.cfgDFormItems = newParagraph.cfgDFormItems.map(originalItem => {
                const newGuid = newGuids[guidIndex++];
                itemIdMap.set(originalItem.DFormItemId, newGuid);
                const newItem = { ...originalItem };
                newItem.DFormItemId = newGuid;
                newItem.DFormParagraphId = newParagraph.DFormParagraphId;
                // Clear master item reference so a new one gets created on save
                newItem.DFormMasterItemId = null;
                newItem.cfgDFormMasterItem = null;
                // Strip navigation properties â€” EF treats non-null nav objects as new INSERT targets
                newItem.Calculation = null;
                newItem.BeforeDisplayItemAction = null;
                newItem.AfterUpdateItemAction = null;
                newItem.ValidValue = null;
                newItem.ChildDFormItems = [];
                newItem.enumFormControl = null;
                newItem.enumLabelAligment = null;
                newItem.enumPostLabelAligment = null;
                return newItem;
              });
            }

            // Fix RepeatDFormItem references â€” scalar FK and navigation property must
            // both point to the new item GUID, not the old one
            if (newParagraph.RepeatDFormItemId) {
              const newRepeatId = itemIdMap.get(newParagraph.RepeatDFormItemId);
              if (newRepeatId) {
                newParagraph.RepeatDFormItemId = newRepeatId;
                if (newParagraph.RepeatDFormItem) {
                  newParagraph.RepeatDFormItem = newParagraph.cfgDFormItems?.find(
                    i => i.DFormItemId === newRepeatId
                  ) ?? null;
                }
              } else {
                newParagraph.RepeatDFormItemId = null;
                newParagraph.RepeatDFormItem = null;
              }
            }

            // Remap ParentDFormItemId so child items reference the new item IDs
            if (newParagraph.cfgDFormItems) {
              newParagraph.cfgDFormItems.forEach(item => {
                if (item.ParentDFormItemId) {
                  item.ParentDFormItemId = itemIdMap.get(item.ParentDFormItemId) ?? null;
                }
              });
            }

            // Strip paragraph navigation properties â€” EF treats non-null nav objects as new INSERT targets
            newParagraph.enumParagraphOrientation = null;
            newParagraph.enumItemLayout = null;

            return newParagraph;
          });

          resolve(newVersion);
        })
        .catch(err => reject(err));
    });
  }

  saveDuplicatedForm(form: FormConfigurationModel): Promise<FormConfigurationModel> {
    return new Promise((resolve, reject) => {
      this.builderService.saveDuplicatedCfgDForm(form)
        .subscribe(
          response => resolve(response),
          err => reject(err)
        );
    });
  }

  saveDuplicatedVersion(version: FormVersionConfigurationModel): Promise<any> {
    return new Promise((resolve, reject) => {
      this.builderService.saveDuplicateCfgVersion(version)
        .subscribe(
          response => resolve(response),
          err => reject(err)
        );
    });
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    if (this.isSelectingActiveVersion) {
      this.isSelectingActiveVersion = false;
    }
  }

  enterVersionSelectionMode(): void {
    this.isSelectingActiveVersion = true;
  }

  cancelVersionSelection(): void {
    this.isSelectingActiveVersion = false;
  }

  handleVersionSelectionClick(version: FormVersionConfigurationModel): void {
    const statusName = this.getVersionStatusName(version.enumDFormBuildStatusID, version);
    if (statusName === 'Active') {
      this.setVersionDraft(version);
    } else {
      this.setVersionActive(version);
    }
    this.isSelectingActiveVersion = false;
  }

  setVersionActive(version: FormVersionConfigurationModel): void {
    // Find the currently active version (if any), excluding the version being promoted
    const activeVersionNumber = this.getActiveVersionNumber();
    const currentlyActiveVersion = (activeVersionNumber !== null)
      ? this.selectedCfgForm.cfgDFormVersions.find(v =>
          v.DFormVersionId !== version.DFormVersionId &&
          v.Version === activeVersionNumber &&
          v.enumDFormBuildStatusID?.toLowerCase() === this.globals.FORM_STATUS_APPROVED?.toLowerCase()
        )
      : null;

    const previousStatusId = version.enumDFormBuildStatusID;
    version.enumDFormBuildStatusID = this.globals.FORM_STATUS_APPROVED;

    // Step 1: Demote the old active version locally and persist to DB
    const demoteOldActive = currentlyActiveVersion
      ? (() => {
          const previousActiveStatus = currentlyActiveVersion.enumDFormBuildStatusID;
          currentlyActiveVersion.enumDFormBuildStatusID = this.globals.FORM_STATUS_DRAFT;
          return this.saveVersion(currentlyActiveVersion).catch(err => {
            currentlyActiveVersion.enumDFormBuildStatusID = previousActiveStatus;
            throw err;
          });
        })()
      : Promise.resolve(null);

    // Step 2: Promote the new version and persist to DB
    demoteOldActive
      .then(() => this.saveVersion(version))
      .then(() => {
        this.stateService.setSelectedForm(this.selectedCfgForm);
        this.showMessage('Success', `v${version.Version}.${version.Revision} is now active`);
      })
      .catch(err => {
        version.enumDFormBuildStatusID = previousStatusId;
        this.showMessage('Error', 'Error setting version as active');
        console.error(err);
      });
  }

  setVersionDraft(version: FormVersionConfigurationModel): void {
    const isActive = this.getVersionStatusName(version.enumDFormBuildStatusID, version) === 'Active';
    const message = isActive
      ? `Set v${version.Version}.${version.Revision} to Draft? This form will have no active version until you promote another one.`
      : `Set v${version.Version}.${version.Revision} to Draft?`;

    this.confirm(message).then(confirmed => {
      if (!confirmed) return;

      const previousStatusId = version.enumDFormBuildStatusID;
      version.enumDFormBuildStatusID = this.globals.FORM_STATUS_DRAFT;

      this.saveVersion(version)
        .then(() => {
          this.stateService.setSelectedForm(this.selectedCfgForm);
          this.showMessage('Success', `v${version.Version}.${version.Revision} set to Draft`);
        })
        .catch(err => {
          version.enumDFormBuildStatusID = previousStatusId;
          this.showMessage('Error', 'Error setting version as draft');
          console.error(err);
        });
    });
  }

  saveVersion(version: FormVersionConfigurationModel): Promise<FormVersionConfigurationModel> {
    return new Promise((resolve, reject) => {
      const versionClone = JSON.parse(JSON.stringify(version));
      versionClone.cfgDFormParagraphs = [];
      versionClone.enumDFormBuildStatus = null;

      this.builderService.saveCfgFormVersion(versionClone)
        .subscribe(
          response => resolve(response),
          err => reject(err)
        );
    });
  }

  // Trash / Recently Deleted methods
  switchToTrashView(): void {
    this.isTrashView = true;
    this.selectedCfgForm = null;
    this.selectedCfgFormPreviewId = null;
    this.selectedTrashEntry = null;
  }

  switchToAllForms(): void {
    this.isTrashView = false;
    this.selectedTrashEntry = null;
  }

  private updateDerivedTrashLists(): void {
    this.deletedFormEntries = this.trashEntries.filter(e => e.type === 'form');

    const map = new Map<string, { formName: string; formId: string; versions: TrashEntry[] }>();
    this.trashEntries
      .filter(e => e.type === 'version')
      .forEach(entry => {
        if (!map.has(entry.parentFormId)) {
          map.set(entry.parentFormId, {
            formName: entry.parentFormName,
            formId: entry.parentFormId,
            versions: []
          });
        }
        map.get(entry.parentFormId).versions.push(entry);
      });
    this.deletedVersionsGrouped = Array.from(map.values());
  }

  selectTrashEntry(entry: TrashEntry): void {
    this.selectedTrashEntry = entry;
    this.selectedCfgForm = null;
    this.selectedCfgFormPreviewId = null;
  }

  restoreForm(entry: TrashEntry): void {
    this.confirm('Restore this form and all its versions?').then(result => {
      if (!result) return;

      this.isLoading = true;
      this.formsService.getCfgDForm(entry.id).subscribe(
        (form: FormConfigurationModel) => {
          form.xauditDeleted = false;
          const formClone = JSON.parse(JSON.stringify(form));
          formClone.cfgDFormVersions = [];
          formClone.enumFormCategory = null;
          formClone.enumFormDataSource = null;
          formClone.enumCustomForm = null;

          this.builderService.saveCfgDForm(formClone).subscribe(
            () => {
              // Also restore all soft-deleted versions for this form
              const deletedVersions = (form.cfgDFormVersions || []).filter(v => v.xauditDeleted);
              const versionRestorePromises = deletedVersions.map(version => {
                version.xauditDeleted = false;
                return this.saveVersion(version);
              });

              Promise.all(versionRestorePromises).then(() => {
                this.trashService.removeEntriesForForm(entry.id);
                this.isLoading = false;
                if (!this.isDestroyed) { this.cdr.detectChanges(); }
                this.selectedTrashEntry = null;
                this.isTrashView = false;
                this.init();
                this.showMessage('Success', 'Form restored');
              }).catch(err => {
                // Form was restored but some versions failed — still clean up trash and refresh
                this.trashService.removeEntriesForForm(entry.id);
                this.isLoading = false;
                if (!this.isDestroyed) { this.cdr.detectChanges(); }
                this.selectedTrashEntry = null;
                this.isTrashView = false;
                this.init();
                this.showMessage('Error', 'Form restored but some versions could not be restored');
              });
            },
            err => {
              this.isLoading = false;
              if (!this.isDestroyed) { this.cdr.detectChanges(); }
              this.showMessage('Error', 'Error restoring form');
            }
          );
        },
        err => {
          this.isLoading = false;
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
          this.showMessage('Error', 'Could not restore form. It may have been permanently deleted.');
        }
      );
    });
  }

  restoreVersion(entry: TrashEntry): void {
    this.confirm('Restore this version?').then(result => {
      if (!result) return;

      this.isLoading = true;
      this.formsService.getCfgDForm(entry.parentFormId).subscribe(
        (form: FormConfigurationModel) => {
          const version = form.cfgDFormVersions?.find(
            v => v.DFormVersionId === entry.id
          );

          if (!version) {
            this.isLoading = false;
            if (!this.isDestroyed) { this.cdr.detectChanges(); }
            this.showMessage('Error', 'Version not found. It may have been permanently deleted.');
            return;
          }

          const activeVersions = (form.cfgDFormVersions || []).filter(v => !v.xauditDeleted);

          // Block restore if a non-deleted version already has the same version number.
          const hasVersionConflict = activeVersions.some(v => v.Version === version.Version);
          if (hasVersionConflict) {
            this.isLoading = false;
            if (!this.isDestroyed) { this.cdr.detectChanges(); }
            this.showMessage(
              'Error',
              `Cannot restore v${version.Version}.${version.Revision}: a version with that version number already exists. Delete it first.`
            );
            return;
          }

          // If the version being restored is approved, demote the current active version
          // first to prevent two approved versions coexisting.
          const isRestoringApproved = version.enumDFormBuildStatusID?.toLowerCase() === this.globals.FORM_STATUS_APPROVED?.toLowerCase();
          const currentlyActive = isRestoringApproved
            ? activeVersions.find(v => v.enumDFormBuildStatusID?.toLowerCase() === this.globals.FORM_STATUS_APPROVED?.toLowerCase())
            : null;

          version.xauditDeleted = false;

          const demoteActive: Promise<any> = currentlyActive
            ? (() => {
                currentlyActive.enumDFormBuildStatusID = this.globals.FORM_STATUS_DRAFT;
                return this.saveVersion(currentlyActive);
              })()
            : Promise.resolve(null);

          demoteActive.then(() => this.saveVersion(version)).then(() => {
            this.trashService.removeEntry(entry.id);
            this.isLoading = false;
            if (!this.isDestroyed) { this.cdr.detectChanges(); }
            this.selectedTrashEntry = null;
            this.isTrashView = false;
            this.selectAndFetchCfgForm(entry.parentFormId);
            this.showMessage('Success', 'Version restored');
          }).catch(err => {
            this.isLoading = false;
            if (!this.isDestroyed) { this.cdr.detectChanges(); }
            this.showMessage('Error', 'Error restoring version');
          });
        },
        err => {
          this.isLoading = false;
          if (!this.isDestroyed) { this.cdr.detectChanges(); }
          this.showMessage('Error', 'Could not restore version.');
        }
      );
    });
  }

  restoreSelectedTrashEntry(): void {
    if (!this.selectedTrashEntry) return;
    if (this.selectedTrashEntry.type === 'form') {
      this.restoreForm(this.selectedTrashEntry);
    } else {
      this.restoreVersion(this.selectedTrashEntry);
    }
  }

  clearAllTrash(): void {
    const count = this.trashEntries.length;
    this.confirm('Are you sure you want to clear all recently deleted items? This cannot be undone.')
      .then(first => {
        if (!first) return Promise.resolve(false);
        return this.confirm(`This will permanently remove ${count} item(s) from your recently deleted list. Are you absolutely sure?`);
      })
      .then(second => {
        if (!second) return;
        this.trashService.clearAllTrash();
        this.selectedTrashEntry = null;
        this.showMessage('Success', 'Recently deleted items cleared');
      });
  }
}

